// System Status Dashboard Component
import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Database, Search, Activity } from 'lucide-react';
import InmateService from '../../../services/inmateService';

const DatabaseStatus = () => {
  const [status, setStatus] = useState({
    loading: true,
    databaseOnline: false,
    searchFunctionWorking: false,
    dataAvailable: false,
    lastChecked: null,
    details: null,
    error: null
  });

  const checkSystemStatus = async () => {
    setStatus(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      console.log('🔍 Checking database system status...');
      const result = await InmateService?.testDatabaseConnection();
      
      if (result?.success) {
        setStatus({
          loading: false,
          databaseOnline: true,
          searchFunctionWorking: result?.tests?.searchFunction || false,
          dataAvailable: result?.tests?.dataAvailable || false,
          lastChecked: new Date(),
          details: result,
          error: null
        });
      } else {
        setStatus({
          loading: false,
          databaseOnline: false,
          searchFunctionWorking: false,
          dataAvailable: false,
          lastChecked: new Date(),
          details: result,
          error: result?.error || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('🚨 Status check failed:', error);
      setStatus({
        loading: false,
        databaseOnline: false,
        searchFunctionWorking: false,
        dataAvailable: false,
        lastChecked: new Date(),
        details: null,
        error: error?.message || 'Status check failed'
      });
    }
  };

  useEffect(() => {
    checkSystemStatus();
    // Auto-refresh every 5 minutes
    const interval = setInterval(checkSystemStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (isOnline) => {
    if (status?.loading) return 'text-yellow-500';
    return isOnline ? 'text-green-500' : 'text-red-500';
  };

  const getStatusIcon = (isOnline) => {
    if (status?.loading) return <Activity className="animate-spin" size={20} />;
    return isOnline ? <CheckCircle size={20} /> : <AlertTriangle size={20} />;
  };

  const testSearch = async () => {
    try {
      console.log('🔍 Testing search with "John"...');
      const searchResult = await InmateService?.simpleSearch('John');
      
      if (searchResult?.success) {
        alert(`✅ Search test successful! Found ${searchResult?.data?.length || 0} results for "John"`);
      } else {
        alert(`❌ Search test failed: ${searchResult?.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Search test error: ${error?.message}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Database size={20} />
          Database System Status
        </h3>
        <button
          onClick={checkSystemStatus}
          disabled={status?.loading}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 disabled:opacity-50"
        >
          {status?.loading ? 'Checking...' : 'Refresh Status'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Database Connectivity */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
          <div className={getStatusColor(status?.databaseOnline)}>
            {getStatusIcon(status?.databaseOnline)}
          </div>
          <div>
            <div className="font-medium text-gray-900">Database Connection</div>
            <div className="text-sm text-gray-600">
              {status?.loading ? 'Checking...' : (status?.databaseOnline ? 'Online' : 'Offline')}
            </div>
          </div>
        </div>

        {/* Search Function */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
          <div className={getStatusColor(status?.searchFunctionWorking)}>
            {getStatusIcon(status?.searchFunctionWorking)}
          </div>
          <div>
            <div className="font-medium text-gray-900">Search Function</div>
            <div className="text-sm text-gray-600">
              {status?.loading ? 'Checking...' : (status?.searchFunctionWorking ? 'Working' : 'Failed')}
            </div>
          </div>
        </div>

        {/* Data Availability */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
          <div className={getStatusColor(status?.dataAvailable)}>
            {getStatusIcon(status?.dataAvailable)}
          </div>
          <div>
            <div className="font-medium text-gray-900">Data Available</div>
            <div className="text-sm text-gray-600">
              {status?.loading ? 'Checking...' : (status?.dataAvailable ? 'Yes' : 'No Data')}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {status?.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle size={16} />
            <span className="font-medium">System Error</span>
          </div>
          <div className="text-sm text-red-700 mt-1">{status?.error}</div>
        </div>
      )}

      {/* Search Test Results */}
      {status?.details?.searchResults !== undefined && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-blue-900">Search Test Results</div>
              <div className="text-sm text-blue-700">
                Found {status?.details?.searchResults} results for "John"
              </div>
            </div>
            <button
              onClick={testSearch}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Test Search
            </button>
          </div>
        </div>
      )}

      {/* Last Checked */}
      {status?.lastChecked && (
        <div className="mt-4 text-xs text-gray-500">
          Last checked: {status?.lastChecked?.toLocaleString()}
        </div>
      )}

      {/* Diagnostic Information */}
      {status?.details?.tests && (
        <div className="mt-4 text-xs text-gray-600">
          <strong>Diagnostics:</strong> 
          Connectivity: {status?.details?.tests?.connectivity ? '✅' : '❌'}, 
          Search Function: {status?.details?.tests?.searchFunction ? '✅' : '❌'}, 
          Data Available: {status?.details?.tests?.dataAvailable ? '✅' : '❌'}
        </div>
      )}
    </div>
  );
};

export default DatabaseStatus;